import React, { useState, useMemo, useCallback, memo } from "react";
import "../../styles/team.pc.css";
import "../../styles/table.pc.css";
import Search from "../../UiLib/Search/Search";
import "../../../globalStyles/dataTableStyles.css";
import PersonLineDisplay from "../PersonLineDisplay";
import { initials } from "../../../helpers/Inicials";

export interface ITeamFilter {
    projectId: number;
    role: number[] | undefined;
    name: string | undefined;
}

interface IMember {
    id: number;
    name: string;
    surname: string;
    middleName: string | undefined;
    role: string;
    photoId: number | undefined;
}

interface IPersonSelectProps {
    members: IMember[];
    scrollEnded: boolean;
    loadPage: (skip: number, take: number, filters: ITeamFilter) => void;
    updateFilters: (filters: ITeamFilter) => void;
    selectPerson: (personId: number) => void;
    projectId?: number;
}

const PersonSelector: React.FC<IPersonSelectProps> = memo(({
    members,
    selectPerson
}) => {

    const [searchSubstring, setSearchSubstring] = useState<string>("");

    const membersForDisplay = useMemo(() => {
        const search = searchSubstring?.toLowerCase();
        const searchableFields: (keyof IMember)[] = [
            "name",
            "surname",
            "middleName",
            "role",
        ];

        let filteredMembers = members?.filter((member) => 
            searchableFields.some((field) => 
                typeof member[field] === "string" && 
                String(member[field])?.toLowerCase().includes(search)
            )
        ) || [];

        const membersMap = new Map<number, IMember>();
        
        filteredMembers.forEach((member) => {
            const existingMember = membersMap.get(member.id);
            if (existingMember) {
                membersMap.set(member.id, {
                    ...existingMember,
                    role: `${existingMember.role}, ${member.role}`
                });
            } else {
                membersMap.set(member.id, { ...member });
            }
        });

        return Array.from(membersMap.values());
    }, [members, searchSubstring]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchSubstring(value);
    }, []);

    const handlePersonClick = useCallback((key: number) => {
        selectPerson(key);
    }, [selectPerson]);

    const renderMemberItem = useCallback((member: IMember) => {
        const truncatedRole = member.role.length > 30 
            ? `${member.role.slice(0, 27)}...` 
            : member.role;

        return (
            <div 
                key={member.id} 
                onClick={() => handlePersonClick(member.id)} 
                className="person-selection-item"
            >
                <div className="person-selector-items_new-name">
                    <PersonLineDisplay
                        name={initials(member.name)}
                        userId={member.id}
                    />
                </div>
                <div title={member.role} className="task-type-text-position ellipses">
                    <a>{truncatedRole}</a>
                </div>
            </div>
        );
    }, [handlePersonClick]);

    return (
        <div className="person-selector-container">
            <div className="widgets_header">
                <Search
                    onSubmitFunc={undefined}
                    value={searchSubstring}
                    changeSearchValue={handleSearchChange}
                />
            </div>
            <div className="person-selector-items_new">
                {membersForDisplay.map(renderMemberItem)}
            </div>
        </div>
    );
});

PersonSelector.displayName = 'PersonSelector';

export default PersonSelector;